<template>
  <section class="container boxWithBorder" style="border-color: #a4393a;">
    <b-message title="Ticket Exchange General Restrictions" aria-close-label="Close message" type="is-warning" :closable="false">
      <ul>
        <li>
          All ticket exchanges must be made before departure of the original
          flight (no exchanges of “partially flown” tickets).
        </li>
        <li>
          Exchanges must be for round-trip/one-way travel only.
        </li>
        <li>
          Changes are limited to change of schedule (dates/times),
          origin/destination, and airline (if applicable).
        </li>
        <li>
          Ticket exchanges apply to flight tickets only. Please note that any
          ancillary services (e.g. upgrades, seat purchases) on the orginal
          booking are not included in the exchange and may be lost.
        </li>
        <li>
          Additional restrictions may be determined by the airline on a per
          ticket basis.
        </li>
      </ul>
    </b-message>

    <div v-if="loaded">
      <div class="message is-default" v-if="this.trip.isNdcBooking && isEligible">
        <div class="message-body">
          <div class="is-size-5 mb-2 has-text-weight-bold">
            This ticket may be eligible for an exchange.
          </div>

          <div v-if="ndcExchangeRules.length > 0">
            <div class="mb-2">
              Please review the rules and restrictions allowed for this ticket:
            </div>
            <ul>
              <li v-for="rule in ndcExchangeRules" :key="rule.metadataKey">
                <strong>{{ rule.ruleId._ }}:</strong> {{ rule.instruction._ }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="message is-success" v-else-if="isEligible">
        <div class="message-body">
          <div class="is-size-5 mb-2 has-text-weight-bold">
            Good news! This ticket is eligible for an exchange.
          </div>
          <div v-if="ticketRules.airlines.length > 0">
            <div class="mb-2">Additional rules apply to this ticket:</div>

            <ul>
              <li>
                The new flight must be booked on the following airlines only:
                {{
                  ticketRules.airlines
                    .map((airline) => {
                      return $helpers.getAirlineName(airline)
                    })
                    .join(", ")
                }}.
              </li>
              <li v-if="ticketRules.penalty == 'Y'">
                An exchange fee may apply.
              </li>

              <li v-if="allowLocationChange === false">
                Departure/Arrival location for the new flights must be the same
                as original ticket.
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div v-else class="message is-danger">
        <div class="message-body">
          <div class="is-size-5 mb-2 has-text-weight-bold">
            Sorry, this ticket is not eligible to be exchanged online at this
            time. If you are in need of reservation support, please contact us
            at 877-924-0303.
          </div>
          <div class="has-text-weight-semibold is-size-6" v-if="reason">
            Additional Details:
          </div>
          <li class="has-text-weight-normal is-size-6" v-if="reason">
            {{ reason }}
          </li>
        </div>
      </div>
    </div>

    <b-button class="button mt-2 mb-5" @click="cancelExchange" icon-right="window-close">
      Cancel
    </b-button>

    <button class="button is-primary mt-2 mb-5 ml-4" @click="ticketCheck" v-if="isEligibilityUnchecked">
      Check eligibility
    </button>

    <b-button class="button is-primary mt-2 mb-5 ml-4" icon-right="arrow-right" @click="pushToSearch" v-if="isEligible">
      Continue
      <b-loading :is-full-page="false" v-model="pending" :can-cancel="true"></b-loading>
    </b-button>

    <b-loading :is-full-page="false" v-model="pending" :can-cancel="true"></b-loading>
  </section>
</template>

<script>
import moment from "moment"

export default {
  name: "AtcTicketCheck",
  props: {
    trip: {
      type: Object,
      required: true,
    },
    tst: {
      type: Object,
      required: true,
    },
  },
  components: {},
  data: function() {
    return {
      loaded: false,
      pending: false,
      reason: "None.",
      exchangeEligible: null, // null = not checked, true = eligible, false = not eligible
      allowLocationChange: null, // null = unknown, true = location change allowed, false = must keep same origin/destination
      ticketRules: {
        general: null, // Only applies to non-NDC tickets
        geo: null, // Only applies to non-NDC tickets
        penalty: null,
        airlines: [],
      },
      ndcInfo: {
        orderItems: [],
        paxList: [],
        journeys: [],
      },
      ndcExchangeRules: [],
      errorMessage: null,
    }
  },
  computed: {
    ticketNumber() {
      return this.tst.ticketNumber.replace(/\D/g, "").substring(0, 13)
    },
    isPastDate() {
      let startDate = new Date(this.trip.startDate)
      let today = new Date()
      return startDate < today
    },
    isVoidable() {
      if (!this.trip.ticketElement) return false

      let now = moment()
      let ticketExpirationDate = moment(
        this.trip.ticketElement.ticket.date,
        "DDMMYY"
      ).add(1, "days")

      if (ticketExpirationDate.isAfter(now)) return true
      else return false
    },
    userDefaultCurrency() {
      return this.$store.getters["travellersAndTrips/getUserDefaultCurrency"]
    },
    // Unified eligibility status that works for both NDC and non-NDC tickets
    isEligible() {
      return this.exchangeEligible === true
    },
    isNotEligible() {
      return this.exchangeEligible === false
    },
    isEligibilityUnchecked() {
      return this.exchangeEligible === null
    },
  },
  methods: {
    //Internal TripCircuit rules
    preCheck() {
      if (this.isPastDate) {
        //disable past dated or "partially flown" tickets for now
        this.reason = "Origin flight has departed."
        return false
      } else if (this.tst.journeys.length > 2) {
        //no multi-city for now
        this.reason = "Flight has more than two legs."
        return false
      } else if (this.trip.tickets.length > 1) {
        //no split ticket or guest tickets on pnr
        this.reason = "Multiple tickets on the same reservation."
        return false
      } else if (this.isVoidable) {
        //no split ticket or guest tickets on pnr
        this.reason =
          "Ticket is still in void window. Please cancel this ticket for a full refund instead of performing an exchange."
        return false
      } else if (this.tst.fare.currency !== this.userDefaultCurrency) {
        this.reason = "Ticket was purchased in a different currency."
        return false
      }
      return true
    },
    async ticketCheck() {
      if (!this.preCheck()) {
        this.pending = false
        this.loaded = true
        this.exchangeEligible = false
        return
      }

      this.pending = true
      if (this.trip.isNdcBooking) {
        await this.ndcCheck()
      } else {
        await this.atcCheck()
      }

      this.loaded = true
      this.pending = false
    },

    async atcCheck() {
      let response = await this.$app.huddleLabsServerCall(
        "amadeus/air/check-atc-eligibility",
        {
          ticketNumbers: Array(this.ticketNumber),
          officeId: this.$globals.profile.officeId,
        }
      )

if (response.data) {
  this.ticketRules.general = response.data.ticketRules?.general
  this.ticketRules.geo = response.data.ticketRules?.geo
  this.ticketRules.penalty = response.data.ticketRules?.penalty

  // Empty array means all carriers are allowed
  this.ticketRules.airlines = response.data.allowedCarriers || []

  this.exchangeEligible = this.ticketRules.general === "Y"
  this.allowLocationChange = this.ticketRules.geo === "Y"
}
    },

    async ndcCheck() {
      try {
        let response = await this.$app.huddleLabsServerCall(
          "amadeus/travel/order-retrieve",
          {
            orderId: this.trip.orderId,
            ownerCode: this.trip.ownerCode,
          }
        )

        if (response.data) {
          // Set NDC-specific values (for compatibility with existing code)
          this.ticketRules.airlines = []

          this.ndcInfo.orderItems = response.data.items
          this.ndcInfo.paxList = response.data.paxList
          this.ndcInfo.journeys = response.data.journeys
          this.ndcInfo.orderId = response.data.orderId
          this.ndcInfo.ownerCode = response.data.ownerCode

          // Extract NDC exchange rules
          if (response.data.rules && Array.isArray(response.data.rules)) {
            this.ndcExchangeRules = response.data.rules
          }

          // Set unified eligibility status for NDC tickets
          this.exchangeEligible = true

          // NDC tickets typically allow location changes (no geo restrictions like non-NDC)
          this.allowLocationChange = true

          // Clear any previous error messages
          this.errorMessage = null
        } else {
          // Handle case where response exists but has no data
          this.errorMessage =
            "Unable to retrieve order information. Please try again or contact support."
          this.exchangeEligible = false
          this.$buefy.toast.open({
            message: this.errorMessage,
            type: "is-danger",
            duration: 5000,
          })
        }
      } catch (error) {
        // Handle API call errors
        this.errorMessage =
          error.message ||
          "An error occurred while retrieving order information. Please try again or contact support."
        this.exchangeEligible = false
        this.$buefy.toast.open({
          message: this.errorMessage,
          type: "is-danger",
          duration: 5000,
        })
        console.error("NDC order retrieve error:", error)
      }
    },

    async displayDoc() {
      this.pending = true
      let response = await this.$app.huddleLabsServerCall(
        "amadeus/air/display-document",
        {
          ticketNumber: this.ticketNumber,
        }
      )
      this.pending = false
    },
    cancelExchange() {
      this.$store.dispatch("bookTravel/setAtcDetails", {})
      this.$parent.close()
      //this.$router.push({ name: "planned-trips" });
    },
    pushToSearch() {
      //set default search status to "C" = "Change"
      //let segments = this.tst.segments.map((segment) => {
      //  return segment;
      //});

      let origin = this.tst.segments[0].origin
      let destination = this.trip.airSummary.destinationAirport.code

      const atcDetails = {
        useAtcShopper: true,
        ticketNumber: this.tst.ticketNumber.replace(/\D/g, "").substring(0, 13),
        airline: this.trip.ticketElement?.ticket?.airlineCode,
        origin: origin,
        destination: destination,
        startDate: this.trip.startDate,
        ticketRules: this.ticketRules,
        endDate: this.trip.endDate,
        price: this.tst.fare.total,
        currency: this.tst.fare.currency,
        isNdcBooking: this.trip.isNdcBooking,
        orderId: this.trip.orderId,
        ownerCode: this.trip.ownerCode,
        ndcInfo: this.ndcInfo,
        creationOfficeId: this.trip.creationOfficeId,
      }

      //If NDC Exchange, use NDC order journey data (includes NDC Segment IDs)
      if (this.ndcInfo.journeys.length > 0) {
        atcDetails.journeys = this.ndcInfo.journeys
        atcDetails.orderId = this.ndcInfo.orderId
        atcDetails.ownerCode = this.ndcInfo.ownerCode
      } else {
        atcDetails.journeys = this.tst.journeys
      }

      this.$store.dispatch("bookTravel/clearBooking")
      this.$store.dispatch("bookTravel/setTripResult", this.trip)
      this.$store.dispatch("bookTravel/setAtcDetails", atcDetails)

      this.$router.push({
        name: "BookingFlights",
      })
    },
  },
}
</script>

<style scoped></style>
